// File: client/src/variants/mayhem/useMayhemRuntime.js
//
// Chess Mayhem runtime. A random global modifier activates EVERY full turn — maximum
// chaos. Modifiers come from the shared platform catalog. Board modifiers mutate the
// committed board (afterMove); rule modifiers set a flag the runtime reads in
// transformLegalMoves for the affected turn. No engine changes.

import React, { useRef, useState, useCallback } from 'react';
import { Flame } from 'lucide-react';
import { createPRNG } from '../../platform/prng';
import { MODIFIERS, pickModifier } from '../../platform/modifiers';

const KNIGHT_OFFSETS = [[-2, -1], [-2, 1], [-1, -2], [-1, 2], [1, -2], [1, 2], [2, -1], [2, 1]];

export function useMayhemRuntime({ config, resumePayload }) {
  // In vs-AI the human is white; the AI plays base chess and is not rule-aware, so
  // rule modifiers targeting the AI are skipped (they would be enforced only via
  // transformLegalMoves, which the AI's move selection ignores — enforcing them
  // one-sidedly would be unfair). Board modifiers always apply to both sides.
  const isVsAI = config.opponent === 'ai';
  const aiColor = 'black';
  const seedRef = useRef(resumePayload?.seed ?? Math.floor(Math.random() * 1e9));
  const rngRef = useRef(createPRNG(seedRef.current));
  const moveCountRef = useRef(resumePayload?.moveCount || 0);
  // Active rule for the CURRENT turn: { rule, color, frozenSquare? }
  const activeRuleRef = useRef(resumePayload?.activeRule || null);
  const [display, setDisplay] = useState({ last: null, active: activeRuleRef.current });

  // Apply a rule modifier for the side about to move (`color`).
  function armRule(mod, color, board) {
    if (mod.rule === 'frozen_random') {
      // Freeze one random piece belonging to `color`.
      const own = [];
      for (let r = 0; r < 8; r++) for (let c = 0; c < 8; c++) {
        const p = board[r][c];
        if (p && p !== '' && (p === p.toUpperCase()) === (color === 'white')) own.push({ row: r, col: c });
      }
      const frozenSquare = own.length ? own[Math.floor(rngRef.current() * own.length)] : null;
      activeRuleRef.current = { rule: 'frozen_random', color, frozenSquare };
    } else {
      activeRuleRef.current = { rule: mod.rule, color };
    }
  }

  const moveMiddleware = {
    transformLegalMoves(gameState, from, engineMoves) {
      const ar = activeRuleRef.current;
      if (!ar || ar.color !== gameState.turn) return engineMoves;
      const board = gameState.board;
      const piece = board[from.row][from.col];
      if (!piece) return engineMoves;
      const isWhite = piece === piece.toUpperCase();

      if (ar.rule === 'frozen_random' && ar.frozenSquare &&
          from.row === ar.frozenSquare.row && from.col === ar.frozenSquare.col) {
        return []; // this piece is frozen this turn
      }

      if (ar.rule === 'knight_only') {
        const out = [];
        for (const [dr, dc] of KNIGHT_OFFSETS) {
          const r = from.row + dr, c = from.col + dc;
          if (r < 0 || r > 7 || c < 0 || c > 7) continue;
          const t = board[r][c];
          if (!t || t === '' || (t === t.toUpperCase()) !== isWhite) {
            out.push({ row: r, col: c, type: t ? 'capture' : 'normal' });
          }
        }
        return out;
      }

      if (ar.rule === 'diagonal_pawns' && piece.toLowerCase() === 'p') {
        // Pawns move diagonally forward onto empty squares, capture straight ahead.
        const dir = isWhite ? -1 : 1;
        const out = [];
        for (const dc of [-1, 1]) {
          const r = from.row + dir, c = from.col + dc;
          if (r >= 0 && r < 8 && c >= 0 && c < 8 && (!board[r][c] || board[r][c] === '')) {
            out.push({ row: r, col: c, type: 'normal' });
          }
        }
        const sr = from.row + dir;
        if (sr >= 0 && sr < 8) {
          const t = board[sr][from.col];
          if (t && t !== '' && (t === t.toUpperCase()) !== isWhite) {
            out.push({ row: sr, col: from.col, type: 'capture' });
          }
        }
        return out;
      }

      return engineMoves;
    },

    afterMove(newState) {
      moveCountRef.current += 1;
      // Clear a spent rule (it applied to the side that just moved).
      if (activeRuleRef.current && activeRuleRef.current.color === newState.turn) {
        activeRuleRef.current = null;
      }

      let message = null;
      // Fire a modifier at the end of every FULL turn (each 2 half-moves).
      if (moveCountRef.current % 2 === 0) {
        const mod = pickModifier(rngRef.current, MODIFIERS);
        if (mod.kind === 'board') {
          message = mod.apply(newState.board, rngRef.current, { newState });
        } else {
          // Rule modifier applies to the side about to move next — but skip it when
          // that side is the AI (see note above), turning it into a harmless "the
          // fates spared the computer" flavour message instead.
          const nextColor = newState.turn === 'white' ? 'black' : 'white';
          if (isVsAI && nextColor === aiColor) {
            message = `${mod.name} fizzled against the computer.`;
          } else {
            armRule(mod, nextColor, newState.board);
            message = mod.apply();
          }
        }
      }

      setDisplay({ last: message, active: activeRuleRef.current });
      return { turnEnded: true };
    },
  };

  const getPersistencePayload = useCallback(() => ({
    seed: seedRef.current,
    moveCount: moveCountRef.current,
    activeRule: activeRuleRef.current,
  }), []);

  const renderOverlay = (slot) => {
    if (slot !== 'top') return null;
    const active = display.active;
    return (
      <div className="flex flex-col items-center gap-1 my-2">
        <span className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border bg-red-700/40 border-red-500/50 text-red-200">
          <Flame size={14} /> MAYHEM — a modifier fires every turn
        </span>
        {active && (
          <span className="text-[11px] text-amber-300">Active rule: {active.rule.replace('_', ' ')}</span>
        )}
        {display.last && <span className="text-[11px] text-slate-300">⚡ {display.last}</span>}
      </div>
    );
  };

  return { moveMiddleware, getPersistencePayload, renderOverlay };
}
